import './styles.scss';

import { faX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, CircularProgress, Modal } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import Input from '../../../../components/input/input';
import RevealSecret from '../../../../components/reveal-secret';
import { ApiKeyWithSecret } from '../../../../providers/api-keys/api-keys-service';
import AuthContext from '../../../../providers/auth/auth-service';
import { messageForError } from '../error-message';
import { labelForPermission } from '../permission-labels';

// `demo` is force-added by the server for demo accounts regardless of request,
// so it's not offered as a selectable scope.
const NON_SELECTABLE_PERMISSIONS = new Set(['demo']);

interface CreateApiKeyModalProps {
  open: boolean;
  handleClose: () => void;
  createApiKey: (name: string, permissions: string[]) => Promise<ApiKeyWithSecret>;
  onCreated: () => void;
}

const CreateApiKeyModal = ({
  open,
  handleClose,
  createApiKey,
  onCreated,
}: CreateApiKeyModalProps) => {
  const { permissions: userPermissions } = useContext(AuthContext);

  const permissionOptions = useMemo(
    () =>
      (userPermissions ?? [])
        .filter((permission) => !NON_SELECTABLE_PERMISSIONS.has(permission))
        .sort((a, b) => labelForPermission(a).localeCompare(labelForPermission(b))),
    [userPermissions],
  );

  // The backend returns `demo` in the permission set deliberately, so the FE can
  // tell a demo user is calling without sniffing the env. Surface it as a notice.
  const isDemoAccount = (userPermissions ?? []).includes('demo');

  const [name, setName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<ApiKeyWithSecret | null>(null);

  // Default to granting the key the caller's full permission set (mirrors their
  // own access); they can pare it down. Seeded on the closed->open edge only:
  // a background permissions refresh (AuthContext re-exchanges the JWT and hands
  // back a fresh array) must NOT re-run this and wipe a selection the user has
  // already pared down while the modal is open.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (justOpened && !result) {
      setSelectedPermissions(permissionOptions);
    }
  }, [open, result, permissionOptions]);

  const resetState = useCallback(() => {
    setName('');
    setSelectedPermissions(permissionOptions);
    setIsLoading(false);
    setErrorMessage(null);
    setResult(null);
  }, [permissionOptions]);

  const handleCloseAndRefresh = useCallback(() => {
    // If a key was minted, refresh the parent list on dismiss.
    if (result) {
      onCreated();
    }
    resetState();
    handleClose();
  }, [handleClose, onCreated, resetState, result]);

  const togglePermission = (value: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(value) ? prev.filter((permission) => permission !== value) : [...prev, value],
    );
  };

  const handleCreate = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const created = await createApiKey(name.trim(), selectedPermissions);
      setResult(created);
    } catch (error) {
      setErrorMessage(messageForError(error, 'Failed to create API key. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const isRevealMode = !!result;

  return (
    <Modal open={open} onClose={handleCloseAndRefresh} className="create-api-key-modal-container">
      <div className="create-api-key-modal-content">
        <div className="header">
          <h1>{isRevealMode ? 'API key created' : 'Create new API key'}</h1>
          <Button className="close-button" onClick={handleCloseAndRefresh}>
            <FontAwesomeIcon icon={faX} />
          </Button>
        </div>

        {isRevealMode ? (
          <>
            <div className="api-key-summary">
              <div className="summary-row">
                <span className="summary-label">Name</span>
                <span className="summary-value">{result?.name}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Client ID</span>
                <span className="summary-value summary-value-mono">{result?.uuid}</span>
              </div>
            </div>

            <RevealSecret secret={result?.secret ?? ''} label="Secret" showWarning />

            <div className="footer-buttons">
              <Button className="confirm-button" onClick={handleCloseAndRefresh}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <>
            {isDemoAccount && (
              <div className="create-api-key-demo-notice" role="note">
                Demo account — API key permissions are restricted.
              </div>
            )}

            <Input
              label="Name"
              placeholder="Create a name for this key"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            <div className="create-api-key-permissions">
              <div className="permission-title">Permissions</div>
              <div className="permission-desc">
                Select the scopes for this key. The server may adjust these to match your own
                permissions.
              </div>
              <div className="feature-permissions-options">
                {permissionOptions.length === 0 ? (
                  <div className="permission-empty">No permissions available on your account.</div>
                ) : (
                  permissionOptions.map((permission) => {
                    const isChecked = selectedPermissions.includes(permission);
                    return (
                      <div className="permission-item" key={permission}>
                        <Checkbox
                          checked={isChecked}
                          onChange={() => togglePermission(permission)}
                          className="permission-checkbox"
                        />
                        <span>{labelForPermission(permission)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {errorMessage && <div className="create-api-key-error">{errorMessage}</div>}

            <div className="footer-buttons">
              <Button
                className="confirm-button"
                onClick={handleCreate}
                disabled={isLoading || name.trim().length === 0}
              >
                {isLoading ? <CircularProgress size={24} /> : <span>Create API key</span>}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CreateApiKeyModal;
